import { get } from "../api.js";
import { getSchoolPupilProfile } from "../schoolPupils.js";
import { useAsync } from "../useAsync.js";

interface ContactDetails {
  studentEmail: string | null;
  parentEmail: string | null;
}

type Props =
  | { kind: "private"; studentId: string; studentName: string }
  | { kind: "school"; pupilId: string; studentName: string; canOpenPupilProfile?: boolean };

async function privateContacts(studentId: string): Promise<ContactDetails> {
  const result = await get<{
    student: { studentEmail: string | null; parentEmail: string | null };
  }>(`/admin/students/${encodeURIComponent(studentId)}`);
  return {
    studentEmail: result.student.studentEmail,
    parentEmail: result.student.parentEmail,
  };
}

async function schoolContacts(pupilId: string): Promise<ContactDetails> {
  // The School profile owns School contact details and exposes the reviewed
  // private bridge separately. Small page sizes keep this identity-only read
  // light while reusing the same authorised profile source the pupil page uses.
  const profile = await getSchoolPupilProfile(pupilId, {
    upcomingLimit: 1,
    historyLimit: 1,
    practiceHistoryLimit: 1,
    repertoireHistoryLimit: 1,
  });
  return {
    studentEmail:
      profile.pupil.email ??
      profile.privateRecord?.schoolEmail ??
      profile.privateRecord?.studentEmail ??
      null,
    parentEmail: profile.privateRecord?.parentEmail ?? null,
  };
}

function emailValue(email: string | null, pending: boolean, failed: boolean) {
  if (pending) return "Loading…";
  if (failed) return "Unavailable";
  if (!email) return "Not recorded";
  return <a href={`mailto:${email}`}>{email}</a>;
}

/**
 * The identity block shared by private and School lesson-note workspaces.
 *
 * The host page exposes the canonical profile link only where its route is
 * available. Contact reads stay behind that profile's existing staff
 * authorisation, and School/private identity is not derived in the browser.
 */
export function LessonStudentIdentity(props: Props) {
  const identityKey = props.kind === "private" ? props.studentId : props.pupilId;
  const contacts = useAsync(
    () =>
      props.kind === "private"
        ? privateContacts(props.studentId)
        : schoolContacts(props.pupilId),
    [props.kind, identityKey],
  );
  const profileHref =
    props.kind === "private"
      ? `/students/${encodeURIComponent(props.studentId)}`
      : `/school-pupils/${encodeURIComponent(props.pupilId)}`;
  const canOpenProfile = props.kind === "private" || props.canOpenPupilProfile === true;

  return (
    <>
      <h1>
        {canOpenProfile ? <a href={profileHref}>{props.studentName}</a> : props.studentName}
      </h1>
      <div className="small muted" aria-label="Student contact details">
        <div>
          <strong>Student email:</strong>{" "}
          {emailValue(contacts.data?.studentEmail ?? null, contacts.loading, contacts.error !== null)}
        </div>
        <div>
          <strong>Parent email:</strong>{" "}
          {emailValue(contacts.data?.parentEmail ?? null, contacts.loading, contacts.error !== null)}
        </div>
      </div>
    </>
  );
}
